import React from 'react';
import { Page, Text, View, Document, StyleSheet, Image, Font } from '@react-pdf/renderer';
import logoamerisa from '../assets/logoamerisazul.png';

// Registrar Times New Roman
Font.register({
  family: 'Times-Roman',
  src: 'https://fonts.gstatic.com/s/timesnewroman/v29/0FlxVP1Tz-TuF4Sv2xufaDV5SbCDUEw.ttf',
});

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 10,
    padding: 20,
    position: 'relative',
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '5px 10px', // Reducido de 10px a 5px en vertical
    marginTop: -2, // Margen negativo para acercarlo más al borde superior
    marginBottom: 0,
  },
  headerText: {
    flex: 1,
    fontSize: 8,
    textAlign: 'center',
    marginLeft: 10, // Espacio entre el logo y el texto
    fontFamily: 'Times-Roman', // Cambiado a Times New Roman
  },
  content: {
    marginTop: 20, // Asegurar espacio suficiente entre el título y el contenido
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 20,
    right: 20,
    textAlign: 'left',
    borderTop: '1px solid #ccc',
    paddingTop: 5,
  },
  section: {
    marginBottom: 20, // Asegurar espacio suficiente para el contenido
  },
  title: {
    fontSize: 18,
    marginBottom: 20, // Asegurar espacio suficiente entre el título y el contenido
    textAlign: 'center',
    fontWeight: 'bold', // Centrar el título "ANTICIPO DE VIAJE" y poner en negritas
  },
  titleBar: {
    backgroundColor: '#000',
    color: '#fff',
    padding: 3, // Mantener delgado
    marginBottom: 15,
    width: 'auto', // Cambiado de 100% a auto para mejor manejo con márgenes negativos
    textAlign: 'center',
    fontSize: 16,
    fontWeight: 'bold',
    marginLeft: -10,
    marginRight: -10,
    paddingLeft: 10,
    paddingRight: 10,
    position: 'relative', // Asegurar posicionamiento correcto
    left: 0,
    right: 0,
  },
  documentFrame: {
    border: '2px solid #000', // Marco más grueso
    padding: 10,
    margin: 5,
    height: '98%',
  },
  text: {
    marginBottom: 5,
    textAlign: 'justify', // Simular texto justificado
    fontSize: 8,
  },
  bold: {
    fontFamily: 'Helvetica-Bold', // Usar la fuente bold específica en lugar de fontWeight
  },
  grid: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  column: {
    flex: 1,
    padding: 5,
  },
  borderBottom: {
    borderBottom: '1px solid #ccc',
    marginBottom: 10,
  },
  space: {
    marginBottom: 20,
  },
  halfPage: {
    width: '100%',
    height: '50%',
    position: 'relative', // Add relative positioning for proper footer placement
  },
  pageDivider: {
    borderBottom: '1px solid #000', // Cambiar a negro sólido
    marginBottom: 10,
    marginLeft: -10, // Para extender la línea hasta el borde del marco
    marginRight: -10, // Para extender la línea hasta el borde del marco
    paddingLeft: 10, // Compensar el marginLeft negativo
    paddingRight: 10, // Compensar el marginRight negativo
  },
  centeredTitle: {
    textAlign: 'center',
    marginTop: 10,
  },
  logo: {
    height: 35,
  },
  footerText: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    position: 'absolute', // Position footer at the bottom of each half-page
    bottom: 20, // Distance from bottom
    left: 20,
    right: 20,
  },
  underline: {
    textDecoration: 'underline',
  },
  summaryBar: {
    backgroundColor: '#000',
    color: '#fff',
    padding: 2,
    marginTop: 10,
    marginBottom: 10,
    width: 'auto',
    textAlign: 'center',
    fontSize: 10,
    fontWeight: 'bold',
    marginLeft: -10,
    marginRight: -10,
    paddingLeft: 10,
    paddingRight: 10,
    position: 'relative',
    left: 0,
    right: 0,
  },
});

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  });
};

const numberToWords = (num) => {
  const unidades = ['', 'un', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho', 'nueve'];
  const especiales = ['diez', 'once', 'doce', 'trece', 'catorce', 'quince', 'dieciséis', 'diecisiete', 'dieciocho', 'diecinueve'];
  const decenas = ['', 'diez', 'veinte', 'treinta', 'cuarenta', 'cincuenta', 'sesenta', 'setenta', 'ochenta', 'noventa'];
  const centenas = ['', 'ciento', 'doscientos', 'trescientos', 'cuatrocientos', 'quinientos', 'seiscientos', 'setecientos', 'ochocientos', 'novecientos'];

  if (num === 0) return 'cero pesos';
  if (num < 0) return 'menos ' + numberToWords(Math.abs(num));

  let words = '';

  // Para miles
  if (num >= 1000) {
    if (Math.floor(num / 1000) === 1) {
      words += 'mil ';
    } else {
      words += numberToWords(Math.floor(num / 1000)) + ' mil ';
    }
    num %= 1000;
  }

  // Para centenas
  if (num >= 100) {
    if (num === 100) {
      words += 'cien';
    } else {
      words += centenas[Math.floor(num / 100)] + ' ';
    }
    num %= 100;
  }

  // Para decenas y unidades
  if (num > 0) {
    if (num < 10) {
      words += unidades[num];
    } else if (num < 20) {
      words += especiales[num - 10];
    } else {
      words += decenas[Math.floor(num / 10)];
      if (num % 10 > 0) {
        if (Math.floor(num / 10) === 2) {
          words = words.replace('veinte', 'veinti');
          words += unidades[num % 10];
        } else {
          words += ' y ' + unidades[num % 10];
        }
      }
    }
  }

  // Añadir "pesos" al final
  return words.trim() + ' pesos';
};

const AnticipoDocument = ({ anticipo }) => {
  const expenseFields = ['viaticos', 'casetas', 'diesel', 'hospedaje', 'maniobras', 'transbordos', 'ferry', 'alimentos', 'combustible', 'deposito', 'permisos', 'otros_gastos', 'pensiones', 'talachas'];

  const totalAnticipo = expenseFields.reduce((total, field) => total + (parseFloat(anticipo[field]) || 0), 0);

  const totalAnticipoText = numberToWords(totalAnticipo);

  // Safely handle undefined origin/destination with explicit fallbacks
  const origen = anticipo.origen || 'No especificado';
  const destino = anticipo.destino || 'No especificado';
  const conceptoAnticipo = `${origen} - ${destino}`;
  const comentarios = anticipo.comentarios || 'N/A';
  const driverName = anticipo.driver && anticipo.driver !== 'undefined undefined' ? anticipo.driver : '________________';
  
  // Use created_dt from the anticipo object if it exists, otherwise use current date
  const generatedDatetime = anticipo.created_dt ? formatDate(anticipo.created_dt) : formatDate(new Date());

  const renderExpenseItems = () => {
    return expenseFields.map(field => {
      if (anticipo[field]) {
        return (
          <Text key={field} style={styles.text}>
            <Text style={styles.bold}>{field}:</Text> ${anticipo[field]}
          </Text>
        );
      }
      return null;
    });
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.documentFrame}>
          {/* First half-page format */}
          <View style={styles.halfPage}>
            <View style={styles.header}>
              <Image src={logoamerisa} style={styles.logo} />
              <Text style={styles.headerText}>
                <Text style={styles.bold}>AL TRANSPORTES PENINSULARES TPE120221Z0{"\n"}</Text>
                CARRETERA PERIFERICO KM 44 No. TABLAJE CATASTRAL 11910{"\n"}
                Col. PERIFERICO PONIENTE MERIDA,YUCATAN, MEXICO C.P. 97300{"\n"}
              </Text>
            </View>
            <Text style={styles.titleBar}>ANTICIPO DE VIAJE</Text>
            
            {/* Fecha justo debajo de la barra de título */}
            <View style={{ alignItems: 'flex-end', marginTop: 1, marginBottom: 5 }}>
              <Text style={styles.text}><Text style={styles.bold}>Fecha:</Text> <Text style={styles.underline}>{generatedDatetime}</Text></Text>
            </View>
            
            <View style={{...styles.content, marginTop: 5}}>
              {/* Número de viaje y anticipo en la misma línea */}
              <View style={styles.grid}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.text}><Text style={styles.bold}>N° de viaje:</Text> <Text style={styles.underline}>{anticipo.shipment}</Text></Text>
                </View>
                <View style={{ flex: 1, alignItems: 'flex-end' }}>
                  <Text style={styles.text}><Text style={styles.bold}>N° de anticipo:</Text> <Text style={styles.underline}>{anticipo.folio}</Text></Text>
                </View>
              </View>
              
              <View style={styles.section}>
                <Text style={[styles.text, { textAlign: 'justify' }]}>
                  Recibi de la empresa: <Text style={styles.underline}>  AL TRANSPORTES PENINSULARES             </Text>{"\n"}
                  {"\n"}
                  con domicilio en: <Text style={styles.underline}> CARRETERA PERIFERICO KM 44 No. TABLAJE CATASTRAL 11910, Col. PERI PONIENTE MERIDA,YUCATAN, C.P. 97300 </Text>{"\n"}
                  {"\n"}
                  la cantidad de: <Text style={styles.underline}>   ${totalAnticipo} ({totalAnticipoText})   </Text>{"\n"}
                  {"\n"}
                  por concepto de anticipo de viaje: <Text style={styles.underline}>{conceptoAnticipo}</Text>
                </Text>
                {/*<View style={styles.summaryBar}>
                  <Text>{anticipo.shipment} / {anticipo.folio} - ${totalAnticipo}</Text>
                </View> */}
                <Text style={[styles.text, { textAlign: 'justify', marginTop: 2 }]}>
                  observaciones: <Text style={styles.text}>{comentarios}</Text>
                </Text>
                <View style={{ borderBottom: '1px solid #ccc', marginTop: 5, marginBottom: 8 }}></View>
                {renderExpenseItems()}
              </View>
            </View>
            <View style={styles.footerText}>
              <Text>Autorizo: _________________</Text>
              <Text>Recibio: <Text style={styles.underline}>   {driverName}   </Text></Text>
            </View>
          </View>

          {/* Divider line between half pages */}
          <View style={styles.pageDivider}></View>

          {/* Second half-page format */}
          <View style={styles.halfPage}>
            <View style={styles.header}>
              <Image src={logoamerisa} style={styles.logo} />
              <Text style={styles.headerText}>
              <Text style={styles.bold}>AL TRANSPORTES PENINSULARES TPE120221Z0{"\n"}</Text>
                CARRETERA PERIFERICO KM 44 No. TABLAJE CATASTRAL 11910{"\n"}
                Col. PERIFERICO PONIENTE MERIDA,YUCATAN, MEXICO C.P. 97300{"\n"}
              </Text>
            </View>
            <Text style={styles.titleBar}>ANTICIPO DE VIAJE</Text>
            
            {/* Fecha justo debajo de la barra de título */}
            <View style={{ alignItems: 'flex-end', marginTop: 1, marginBottom: 5 }}>
              <Text style={styles.text}><Text style={styles.bold}>Fecha:</Text> <Text style={styles.underline}>{generatedDatetime}</Text></Text>
            </View>
            
            <View style={{...styles.content, marginTop: 5}}>
              {/* Número de viaje y anticipo en la misma línea */}
              <View style={styles.grid}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.text}><Text style={styles.bold}>N° de viaje:</Text> <Text style={styles.underline}>{anticipo.shipment}</Text></Text>
                </View>
                <View style={{ flex: 1, alignItems: 'flex-end' }}>
                  <Text style={styles.text}><Text style={styles.bold}>N° de anticipo:</Text> <Text style={styles.underline}>{anticipo.folio}</Text></Text>
                </View>
              </View>
              
              <View style={styles.section}>
                <Text style={[styles.text, { textAlign: 'justify' }]}>
                  Recibi de la empresa: <Text style={styles.underline}>  AL TRANSPORTES PENINSULARES             </Text>{"\n"}
                  {"\n"}
                  con domicilio en: <Text style={styles.underline}> CARRETERA PERIFERICO KM 44 No. TABLAJE CATASTRAL 11910, Col. PERI PONIENTE MERIDA,YUCATAN, C.P. 97300 </Text>{"\n"}
                  {"\n"}
                  la cantidad de: <Text style={styles.underline}>   ${totalAnticipo} ({totalAnticipoText})   </Text>{"\n"}
                  {"\n"}
                  por concepto de anticipo de viaje: <Text style={styles.underline}>{conceptoAnticipo}</Text>
                </Text>
                {/*<View style={styles.summaryBar}>
                  <Text>{anticipo.shipment} / {anticipo.folio} - ${totalAnticipo}</Text>
                </View> */}
                <Text style={[styles.text, { textAlign: 'justify', marginTop: 2 }]}>
                  observaciones: <Text style={styles.text}>{comentarios}</Text>
                </Text>
                <View style={{ borderBottom: '1px solid #ccc', marginTop: 5, marginBottom: 8 }}></View>
              </View>
            </View>
            <View style={styles.footerText}>
              <Text>Autorizo: _________________</Text>
              <Text>Recibio: <Text style={styles.underline}>   {driverName}   </Text></Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default AnticipoDocument;